import React, { useCallback, useMemo } from 'react';
import { Button, Column, Row, Text } from '@ydistri/ds';
import { isModalViewMode, ViewMode } from './scopeUtilsLib';
import { iterableConfigurationRuleScopeEntityType } from '../../../helpers/types/iterableEnums';
import { MdWarning } from 'react-icons/md';
import { Popover } from 'antd';
import { updateScopeEntitySelectedIds } from './scopeConfigurationSlice';
import { useOpenScopeModal } from './useOpenScopeModal';
import { ReduxState, useAppDispatch } from '../../../store';
import { TemplateOrCalculationWithValue } from '../../../apis/api';
import { useStoresReport } from './useStoresReport';
import { missingEntities } from './storesReportLib';
import { useScope } from './useScope';
import { useSelector } from 'react-redux';

interface ScopeMissingEntityViewProps {
  scopeId: number;
  viewMode: ViewMode;
  templateOrCalculation: TemplateOrCalculationWithValue;
}

/**
 * The selected ids of a rule that its parent scope filters out, as the server reports them, with
 * the one action that removes them from the selection.
 */
const ScopeMissingEntityView: React.FC<ScopeMissingEntityViewProps> = ({
  scopeId,
  viewMode,
  templateOrCalculation,
}) => {
  const dispatch = useAppDispatch();
  const { problems } = useStoresReport(templateOrCalculation);
  const scopeById = useScope(scopeId, templateOrCalculation);
  const modalScope = useSelector((state: ReduxState) => state.scope.modalScope);
  const scope = isModalViewMode(viewMode) ? modalScope : scopeById;

  const openModalHandler = useOpenScopeModal(scope);

  const missing = useMemo(() => missingEntities(problems, scopeId), [problems, scopeId]);
  const text = missing.messages.join(' ');

  const fixOutOfScopeDataHandler = useCallback(() => {
    iterableConfigurationRuleScopeEntityType.forEach(et => {
      const outOfScope = missing.ids[et];
      if (outOfScope.length > 0 && scope) {
        dispatch(
          updateScopeEntitySelectedIds({
            entityType: et,
            selectedIds: scope.entities[et].selectedIds.filter(id => !outOfScope.includes(id)),
          }),
        );
      }
    });
  }, [dispatch, missing.ids, scope]);

  if (missing.totalCount === 0) return null;

  if (viewMode === ViewMode.EXCEPTION) {
    return (
      <Popover content={text}>
        <Button onClick={openModalHandler} $colorVariant="selected">
          <MdWarning />
          Warning
        </Button>
      </Popover>
    );
  }

  return (
    <Column $gap="0.5rem">
      <Row $gap="0.25rem" $flexGrow={1} $flexWrap="wrap" $alignItems="center">
        <Text $type="danger">{text}</Text>
        <Button onClick={fixOutOfScopeDataHandler} $sizeVariant="small" $colorVariant="ghostDanger">
          Clean up scope
        </Button>
      </Row>
    </Column>
  );
};

export default ScopeMissingEntityView;
